import { ReactNode, useRef, useState } from 'react';
import { urlFile } from '../../../../AxiosConfig';
import { HIGHLIGHT_ICONS } from '../../../../establishment-page/highlightIcons';
import { GALLERY_ACCEPT } from './PaginaGaleria';
import { SettingsRow, SettingsSection } from './SettingsGroup';
import { Plus } from 'lucide-react';

export type PageHighlight = { icon: string; title: string; text: string };
export type PageTestimonial = { name: string; text: string };

export type PageContent = {
    coverImageId: string;
    title: string;
    description: string;
    about: string;
    aboutImageId: string;
    highlights: PageHighlight[];
    testimonials: PageTestimonial[];
};

// Limites espelhados em LandingPagesService (normalizeText/normalizeHighlights/normalizeTestimonials).
export const MAX_HIGHLIGHTS = 4;
export const MAX_TESTIMONIALS = 6;

type PaginaConteudoProps = {
    content: PageContent;
    disabled: boolean;
    onChange: (patch: Partial<PageContent>) => void;
    onUploadImage: (file: File) => Promise<string | null>;
};

const Counter = ({ value, max }: { value: string; max: number }) => (
    <span className={value.length >= max ? 'text-error' : ''}>
        {value.length}/{max}
    </span>
);

const ItemHeader = ({ label, disabled, onRemove, children }: { label: string; disabled: boolean; onRemove: () => void; children?: ReactNode }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-base-content/55">{label}</span>
        {children}
        <button type="button" className="estab-link-button estab-link-button--destructive" disabled={disabled} onClick={onRemove}>
            Remover
        </button>
    </div>
);

// Foto unica (capa ou Sobre): previa com trocar e remover.
function SinglePhoto({ imageId, emptyHint, disabled, wide, onChange, onUpload }: { imageId: string; emptyHint: string; disabled: boolean; wide?: boolean; onChange: (id: string) => void; onUpload: (file: File) => Promise<string | null> }) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [busy, setBusy] = useState(false);
    const aspect = wide ? 'aspect-[16/9] sm:max-w-sm' : 'aspect-[4/3] sm:max-w-xs';

    const handleFile = async (file?: File) => {
        if (!file) return;
        setBusy(true);
        const id = await onUpload(file);
        setBusy(false);
        if (id) onChange(id);
    };

    return (
        <div className="w-full">
            <input
                ref={inputRef}
                type="file"
                accept={GALLERY_ACCEPT.join(',')}
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    void handleFile(file);
                }}
            />
            {imageId || busy ? (
                <div className={`relative w-full overflow-hidden rounded-[0.625rem] bg-base-200 ${aspect}`}>
                    {imageId && <img src={`${urlFile}/${encodeURIComponent(imageId)}`} alt="" className="h-full w-full object-cover" />}
                    {busy && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                            <span className="loading loading-spinner loading-sm"></span>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => inputRef.current?.click()}
                    className={`estab-gallery-add flex w-full flex-col items-center justify-center gap-1.5 rounded-[0.625rem] text-[rgb(var(--sf-nav-accent-rgb))] disabled:opacity-40 ${aspect}`}
                >
                    <Plus className="text-lg" aria-hidden="true" />
                    <span className="text-xs font-medium">Adicionar foto</span>
                </button>
            )}
            {imageId ? (
                <div className="mt-2 flex gap-4">
                    <button type="button" className="estab-link-button" disabled={disabled || busy} onClick={() => inputRef.current?.click()}>Trocar</button>
                    <button type="button" className="estab-link-button estab-link-button--destructive" disabled={disabled || busy} onClick={() => onChange('')}>Remover</button>
                </div>
            ) : (
                <p className="mt-2 text-xs text-base-content/55">{emptyHint}</p>
            )}
        </div>
    );
}

export function PaginaConteudo({ content, disabled, onChange, onUploadImage }: PaginaConteudoProps) {
    const { highlights, testimonials } = content;

    const updateHighlight = (index: number, patch: Partial<PageHighlight>) =>
        onChange({ highlights: highlights.map((item, position) => (position === index ? { ...item, ...patch } : item)) });
    const updateTestimonial = (index: number, patch: Partial<PageTestimonial>) =>
        onChange({ testimonials: testimonials.map((item, position) => (position === index ? { ...item, ...patch } : item)) });

    return (
        <>
            <SettingsSection
                title="Apresentação"
                footer="Primeira coisa que o cliente vê. Sem foto de capa, a página usa a primeira foto da galeria."
            >
                <SettingsRow label="Foto de capa" description="Horizontal e bem iluminada.">
                    <SinglePhoto
                        wide
                        imageId={content.coverImageId}
                        emptyHint="Usando a primeira foto da galeria, se houver."
                        disabled={disabled}
                        onChange={(coverImageId) => onChange({ coverImageId })}
                        onUpload={onUploadImage}
                    />
                </SettingsRow>
                <SettingsRow label="Frase de destaque" htmlFor="pageHeadline" description={<Counter value={content.title} max={120} />}>
                    <input
                        id="pageHeadline"
                        type="text"
                        maxLength={120}
                        className="input estab-field"
                        placeholder="A experiência que seu estilo merece."
                        value={content.title}
                        onChange={(event) => onChange({ title: event.target.value })}
                    />
                </SettingsRow>
                <SettingsRow label="Subtítulo" htmlFor="pageSubtitle" description={<Counter value={content.description} max={240} />}>
                    <input
                        id="pageSubtitle"
                        type="text"
                        maxLength={240}
                        className="input estab-field"
                        placeholder="Cortes, barba e cuidado com atenção a cada detalhe."
                        value={content.description}
                        onChange={(event) => onChange({ description: event.target.value })}
                    />
                </SettingsRow>
            </SettingsSection>

            <SettingsSection title="Sobre" footer="A seção aparece quando o texto está preenchido. Sem foto própria, usa uma foto da galeria.">
                <SettingsRow stacked>
                    <textarea
                        aria-label="Texto sobre o estabelecimento"
                        maxLength={3000}
                        className="textarea estab-field !ml-0 !min-h-[9rem] !w-full"
                        placeholder="Conte a história do estabelecimento, a experiência da equipe e o que torna o atendimento especial."
                        value={content.about}
                        onChange={(event) => onChange({ about: event.target.value })}
                    />
                </SettingsRow>
                <SettingsRow label="Foto" description="De preferência o ambiente ou a equipe.">
                    <SinglePhoto
                        imageId={content.aboutImageId}
                        emptyHint="Usando uma foto da galeria, se houver."
                        disabled={disabled}
                        onChange={(aboutImageId) => onChange({ aboutImageId })}
                        onUpload={onUploadImage}
                    />
                </SettingsRow>
            </SettingsSection>

            <SettingsSection
                title={`Diferenciais · ${highlights.length} de ${MAX_HIGHLIGHTS}`}
                footer="Aparecem logo abaixo da capa. Ex.: café por conta da casa, estacionamento, Pix e cartão."
            >
                {highlights.map((item, index) => (
                    <SettingsRow stacked key={index}>
                        <div className="w-full space-y-2">
                            <ItemHeader label={`Diferencial ${index + 1}`} disabled={disabled} onRemove={() => onChange({ highlights: highlights.filter((_, position) => position !== index) })} />
                            <div className="flex flex-wrap gap-1" role="group" aria-label={`Ícone do diferencial ${index + 1}`}>
                                {HIGHLIGHT_ICONS.map((icon) => {
                                    const selected = (item.icon || HIGHLIGHT_ICONS[0].key) === icon.key;
                                    return (
                                        <button
                                            key={icon.key}
                                            type="button"
                                            title={icon.label}
                                            aria-label={icon.label}
                                            aria-pressed={selected}
                                            onClick={() => updateHighlight(index, { icon: icon.key })}
                                            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors ${selected ? 'bg-primary text-primary-content' : 'text-base-content/55 hover:bg-base-content/[0.07]'}`}
                                        >
                                            <icon.Icon aria-hidden="true" />
                                        </button>
                                    );
                                })}
                            </div>
                            <input
                                type="text"
                                aria-label={`Título do diferencial ${index + 1}`}
                                maxLength={40}
                                className="input estab-field !ml-0 !w-full !bg-[var(--estab-fill)]"
                                placeholder="Título (obrigatório)"
                                value={item.title}
                                onChange={(event) => updateHighlight(index, { title: event.target.value })}
                            />
                            <input
                                type="text"
                                aria-label={`Descrição do diferencial ${index + 1}`}
                                maxLength={120}
                                className="input estab-field !ml-0 !w-full !bg-[var(--estab-fill)]"
                                placeholder="Descrição curta (opcional)"
                                value={item.text}
                                onChange={(event) => updateHighlight(index, { text: event.target.value })}
                            />
                        </div>
                    </SettingsRow>
                ))}
                <SettingsRow inline>
                    <button
                        type="button"
                        className="estab-link-button"
                        disabled={disabled || highlights.length >= MAX_HIGHLIGHTS}
                        onClick={() => onChange({ highlights: [...highlights, { icon: HIGHLIGHT_ICONS[0].key, title: '', text: '' }] })}
                    >
                        <Plus className="text-[0.7rem]" aria-hidden="true" />
                        Adicionar diferencial
                    </button>
                </SettingsRow>
            </SettingsSection>

            <SettingsSection
                title={`Depoimentos · ${testimonials.length} de ${MAX_TESTIMONIALS}`}
                footer="Publique apenas depoimentos reais, com autorização do cliente."
            >
                {testimonials.map((item, index) => (
                    <SettingsRow stacked key={index}>
                        <div className="w-full space-y-2">
                            <ItemHeader label={`Depoimento ${index + 1}`} disabled={disabled} onRemove={() => onChange({ testimonials: testimonials.filter((_, position) => position !== index) })} />
                            <input
                                type="text"
                                aria-label={`Nome do cliente do depoimento ${index + 1}`}
                                maxLength={60}
                                className="input estab-field !ml-0 !w-full !bg-[var(--estab-fill)]"
                                placeholder="Nome do cliente"
                                value={item.name}
                                onChange={(event) => updateTestimonial(index, { name: event.target.value })}
                            />
                            <textarea
                                aria-label={`Texto do depoimento ${index + 1}`}
                                maxLength={400}
                                className="textarea estab-field !ml-0 !min-h-[5.5rem] !w-full"
                                placeholder="O que o cliente disse"
                                value={item.text}
                                onChange={(event) => updateTestimonial(index, { text: event.target.value })}
                            />
                        </div>
                    </SettingsRow>
                ))}
                <SettingsRow inline>
                    <button
                        type="button"
                        className="estab-link-button"
                        disabled={disabled || testimonials.length >= MAX_TESTIMONIALS}
                        onClick={() => onChange({ testimonials: [...testimonials, { name: '', text: '' }] })}
                    >
                        <Plus className="text-[0.7rem]" aria-hidden="true" />
                        Adicionar depoimento
                    </button>
                </SettingsRow>
            </SettingsSection>
        </>
    );
}
